import { useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';

import { GateRedirect } from '@/components/GateRedirect';
import { ROUTES, type OwnProfileEditSection } from '@/constants/routes';
import { userIdAtom } from '@/services/globalState';

/**
 * Whether a profile route's id names the signed-in reader, by number or by `me`.
 */
export function isOwnProfileId(
  routeId: string | string[] | undefined,
  currentUserId: number | string | null | undefined
): boolean {
  const id = Array.isArray(routeId) ? routeId[0] : routeId;

  return !!currentUserId && (id === 'me' || id === String(currentUserId));
}

/**
 * Keeps an old `/profile/[id]/<section>` edit link working now that your own profile and its
 * edit screens live in Account.
 *
 * The owner goes to the edit screen in Account, so Back returns to Account rather than to a
 * Directory tab they never opened. Anyone else goes to the profile they asked about. It renders
 * nothing until the reader's id is known, since the secure layout signs a reader in first and the
 * id arrives moments later.
 *
 * This is client-side reachability, not authorization. The API stays the enforcement boundary for
 * who may change a profile.
 */
export function ownProfileEditRedirect(section: OwnProfileEditSection) {
  function OwnProfileEditRedirect() {
    const { id } = useLocalSearchParams();

    const currentUserId = useAtomValue(userIdAtom);

    if (!currentUserId) {
      return null;
    }

    if (isOwnProfileId(id, currentUserId)) {
      return <GateRedirect href={ROUTES.ACCOUNT_PROFILE_EDIT(section) as string} />;
    }

    return <GateRedirect href={`/profile/${Array.isArray(id) ? id[0] : id}`} />;
  }

  return OwnProfileEditRedirect;
}
